import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { getBrokers, searchContent } from '../../lib/api';

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brokersOpen, setBrokersOpen] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    getBrokers().then(res => setBrokers(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setBrokersOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    setBrokersOpen(false);
    setMobileOpen(false);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, [location.pathname]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.trim().length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceTimer.current = setTimeout(async () => {
      try {
        const res = await searchContent(query);
        setSearchResults(res.data.results || []);
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
  }, []);

  const handleSearchSelect = (url) => {
    navigate(url);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const links = [
    { to: '/', label: 'Home' },
    { to: '/compare', label: 'Compare' },
    { to: '/guides', label: 'Guides' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;
  const isBrokerActive = brokers.some(b => location.pathname === `/${b.slug}`);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-xl" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="text-xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Markets<span className="text-blue-600">Review</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive('/') ? 'text-slate-900 bg-slate-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
              data-testid="nav-link-home"
            >
              Home
            </Link>

            {/* Brokers Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setBrokersOpen(!brokersOpen)}
                className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isBrokerActive ? 'text-slate-900 bg-slate-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
                data-testid="nav-brokers-dropdown"
              >
                Brokers <ChevronDown className={`w-3.5 h-3.5 transition-transform ${brokersOpen ? 'rotate-180' : ''}`} />
              </button>
              {brokersOpen && (
                <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1.5 z-50" data-testid="brokers-dropdown-menu">
                  {brokers.map(b => (
                    <Link
                      key={b.slug}
                      to={`/${b.slug}`}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        isActive(`/${b.slug}`)
                          ? 'text-slate-900 bg-slate-50 font-medium'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                      }`}
                      data-testid={`nav-broker-${b.slug}`}
                    >
                      <span className="font-medium">{b.name}</span>
                    </Link>
                  ))}
                  <div className="border-t border-slate-100 mt-1.5 pt-1.5">
                    <Link
                      to="/compare"
                      className="block px-4 py-2 text-sm text-blue-600 font-medium hover:bg-blue-50 transition-colors"
                      data-testid="nav-compare-all"
                    >
                      Compare All Brokers
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {links.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.to) ? 'text-slate-900 bg-slate-100' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <div className="hidden md:flex items-center gap-2" ref={searchRef}>
            {searchOpen ? (
              <div className="relative">
                <div className="flex items-center bg-slate-100 rounded-lg border border-slate-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                  <Search className="w-4 h-4 text-slate-400 ml-3" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search brokers, guides..."
                    className="w-56 bg-transparent px-2 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                    autoFocus
                    data-testid="search-input"
                  />
                  <button onClick={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]); }} className="p-1.5 mr-1 text-slate-400 hover:text-slate-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                {(searchResults.length > 0 || searching) && (
                  <div className="absolute top-full right-0 mt-1.5 w-80 bg-white border border-slate-200 rounded-xl shadow-xl py-2 z-50 max-h-96 overflow-y-auto" data-testid="search-results-dropdown">
                    {searching && <div className="px-4 py-3 text-xs text-slate-400">Searching...</div>}
                    {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                      <div className="px-4 py-3 text-xs text-slate-400">No results found</div>
                    )}
                    {searchResults.map((r, i) => (
                      <button
                        key={`${r.type}-${r.slug}-${i}`}
                        onClick={() => handleSearchSelect(r.url)}
                        className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors"
                        data-testid={`search-result-${r.slug}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            r.type === 'broker' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>{r.type}</span>
                          <span className="text-sm font-medium text-slate-900 truncate">{r.title}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-1 pl-0">{r.excerpt}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="search-toggle-btn"
                aria-label="Search"
              >
                <Search className="w-4.5 h-4.5" />
              </button>
            )}
          </div>

          <button
            className="md:hidden p-2 text-slate-600 hover:text-slate-900"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-nav">
          <div className="px-4 py-3 space-y-1">
            {/* Mobile Search */}
            <div className="relative mb-3">
              <div className="flex items-center bg-slate-100 rounded-lg border border-slate-200">
                <Search className="w-4 h-4 text-slate-400 ml-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search brokers, guides..."
                  className="w-full bg-transparent px-2 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
                  data-testid="mobile-search-input"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="mt-1 bg-white border border-slate-200 rounded-lg shadow-sm max-h-48 overflow-y-auto">
                  {searchResults.map((r, i) => (
                    <button key={`m-${r.slug}-${i}`} onClick={() => handleSearchSelect(r.url)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b border-slate-100 last:border-0">
                      <span className={`text-[10px] font-bold uppercase mr-2 ${r.type === 'broker' ? 'text-blue-600' : 'text-emerald-600'}`}>{r.type}</span>
                      <span className="text-slate-900 font-medium">{r.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Link to="/" onClick={() => setMobileOpen(false)}
              className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActive('/') ? 'text-slate-900 bg-slate-100' : 'text-slate-600'}`}>
              Home
            </Link>
            <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">Broker Reviews</div>
            {brokers.map(b => (
              <Link key={b.slug} to={`/${b.slug}`} onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors pl-6 ${
                  isActive(`/${b.slug}`) ? 'text-slate-900 bg-slate-100' : 'text-slate-600'
                }`}
                data-testid={`mobile-broker-${b.slug}`}>
                {b.name}
              </Link>
            ))}
            {links.slice(1).map((link) => (
              <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                className={`block px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${isActive(link.to) ? 'text-slate-900 bg-slate-100' : 'text-slate-600'}`}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
